import { AriaDatapoint, VerbosePerfEventType } from './datapoints/AriaDatapoint';
import { getInitializeAnalyticsPromse } from './initializeAnalytics';
import optionallyTraceDatapoint from './utils/optionallyTraceDatapoint';
import { validatePropertyBag } from './utils/validatePropertyBag';
import isDevEnvironment from './utils/isDevEnvironment';
import getFlightControl from './utils/getFlightControl';
import shouldLogDatapoint from './utils/shouldLogDatapoint';
import { addCommonProperties } from './utils/logSessionDatapoint';
import { isFeatureEnabled } from 'owa-feature-flags';
import { createEvent, logOneDSDatapoint } from './OneDsWrapper';
import { DataPointEventType, GenericKeys } from './types/DatapointEnums';
import { getThroughEdge, getDag, getServerVersion } from 'owa-config';
import isQosDatapoint from './utils/isQosDatapoint';
import { ValueKind, IEventProperty } from '@ms/1ds-analytics-js';

const OwaTestTwoAriaToken =
    '1945726169fc42dfaed4d13706daec06-87bbc157-39fb-4a9d-96e0-c66edc85ccf7-7172';
const OwaTestThreeAriaToken =
    '71915ace70224f8abaecff2c09564a9b-f6358891-6273-4bc6-9fad-6f94b80206d6-7483';

export default function logDatapoint(datapoint: AriaDatapoint, overrideEventType?: string): void {
    getInitializeAnalyticsPromse().then(analyticsOptions => {
        let isFullVerboseLoggingEnabled = isFeatureEnabled('an-full-logging');
        let eventType: string = getEventType(datapoint, overrideEventType);
        let flightControl = getFlightControl(datapoint, analyticsOptions, eventType);
        if (
            !shouldLogDatapoint(datapoint, analyticsOptions, eventType) &&
            !isFullVerboseLoggingEnabled
        ) {
            return;
        }

        datapoint.properties.EventType = 'standard';
        if (isQosDatapoint(analyticsOptions, datapoint)) {
            datapoint.properties.EventType = 'qos';
            datapoint.properties.ThroughAFD = getThroughEdge();
            datapoint.properties.ServiceVersion = getServerVersion();
            datapoint.properties.Dag = getDag();
        }

        if (isFullVerboseLoggingEnabled) {
            datapoint.properties['OriginalEvent'] = eventType;
        }

        const keys = Object.keys(datapoint.properties);
        for (let ii = 0; ii < keys.length; ii++) {
            datapoint.properties[keys[ii]] = datapoint.properties[keys[ii]];
        }

        addCommonProperties(datapoint.properties, analyticsOptions);
        datapoint.properties['Sampled'] = flightControl?.rate ? flightControl.rate : 100;

        if (datapoint.propertyBag) {
            datapoint.properties['MiscData'] = validatePropertyBag(datapoint);
        }

        optionallyTraceDatapoint(datapoint);

        // let's not actually log datapoints if we are gulping or in a branch or a tds box
        // unless the datapoint is explicitly whitelisted
        if (
            !isDevEnvironment(analyticsOptions) ||
            eventType == DataPointEventType.ClientEventDevOnly
        ) {
            const tokens = datapoint.options?.tenantTokens
                ? datapoint.options.tenantTokens
                : analyticsOptions.ariaTenantTokens;
            tokens.forEach(token => logEvent(token, datapoint, eventType));
            if (isFeatureEnabled('an-gzip-exp')) {
                const fiftySplit = Math.round(Math.random());
                const firstToken = fiftySplit ? OwaTestTwoAriaToken : OwaTestThreeAriaToken;
                const secondToken = fiftySplit ? OwaTestThreeAriaToken : OwaTestTwoAriaToken;
                logEvent(firstToken, datapoint, eventType, firstToken == OwaTestThreeAriaToken);
                logEvent(secondToken, datapoint, eventType, secondToken == OwaTestThreeAriaToken);
            }
        }
    });
}

function logEvent(
    token: string,
    datapoint: AriaDatapoint,
    eventType: string,
    shouldGzip?: boolean
) {
    const e = createEvent(
        isFeatureEnabled('an-full-logging') ? VerbosePerfEventType : eventType,
        datapoint.properties
    );

    if (datapoint.properties[GenericKeys.e2eTimeElapsed]) {
        e.latency = <number>datapoint.properties[GenericKeys.e2eTimeElapsed];
    }
    if (e.data) {
        if (datapoint.piiData) {
            e.data['PiiData'] = <IEventProperty>{
                value: datapoint.piiData,
                kind: ValueKind.Pii_Identity,
            };
        }
    }

    logOneDSDatapoint(token, e, undefined, shouldGzip);
}

function getEventType(datapoint: AriaDatapoint, overrideEventType: string | undefined): string {
    if (overrideEventType) {
        return overrideEventType;
    }

    if (datapoint.options) {
        if (datapoint.options.isVerbose) {
            return DataPointEventType.ClientVerbose;
        }

        if (datapoint.options.excludeFromKusto) {
            return DataPointEventType.ClientCosmos;
        }
    }

    return DataPointEventType.ClientEvent;
}
